import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { Person } from "@mui/icons-material";
import CustomTabs, { type TabConfig } from "components/common/CustomTabs";
import SidebarLayout, {
  type SidebarSection,
} from "components/common/SidebarLayout";
import {
  PatientGenetics,
  PatientBiomarkers,
} from "components/features/patient";
import {
  VitalSigns,
  Activities,
  Sleep,
  NutritionAndHydration,
  BodyMeasurements,
  DailyWellBeingScore,
  ReproductiveHealth,
} from "./";
import {
  useAdminPatientAdditionalNotes,
  useAdminPatientEnvironmentToxins,
  useAdminPatientFamilyHistory,
  useAdminPatientFunctionalLifestyle,
  useAdminPatientGeneralInformation,
  useAdminPatientGoalsOfTreatment,
  useAdminPatientMedicalHistory,
  useAdminPatientMedications,
  useAdminPatientNutrition,
  useAdminPatientPresentingConcerns,
  useAdminPatientSocialHistory,
} from "hooks/patientDetail";
import type {
  EnvironmentToxinsResponseDto,
  FamilyHistoryResponseDto,
  FunctionalLifestyleResponseDto,
  GeneralInformationResponseDto,
  GoalsOfTreatmentResponseDto,
  MedicalHistoryResponseDto,
  MedicationStatementResponseDto,
  NutritionAndDietResponseDto,
  PresentingConcernsResponseDto,
  SocialHistoryResponseDto,
} from "libs/cellera-sdk";
import { PatientsPaginationRequestDtoStatusEnum } from "libs/cellera-sdk";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { EPath } from "constants/path";
import type { Patient } from "types";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { formatBirthDate } from "utils/date";
import { formatPatientName } from "utils/patient";

const DEFAULT_EMPTY_VALUE = "Not provided";

interface AdminPatientDetailProps {
  patient: Patient;
  hasRequestedSuccessfullyBefore: boolean;
  onCancelRequest?: () => void;
  onRequest?: () => void;
}

const AdminPatientDetail: React.FC<AdminPatientDetailProps> = ({
  patient,
  hasRequestedSuccessfullyBefore,
  onCancelRequest,
  onRequest,
}) => {
  const navigate = useNavigate();
  const patientId = patient.patientId;

  // Navigation callbacks
  const handleViewVitalSignsHistory = useCallback(() => {
    navigate(EPath.VITAL_SIGNS_HISTORY.replace(":patientId", patientId));
  }, [navigate, patientId]);

  const handleViewActivityHistory = useCallback(() => {
    navigate(EPath.ACTIVITY_HISTORY.replace(":patientId", patientId));
  }, [navigate, patientId]);

  const handleViewSleepHistory = useCallback(() => {
    navigate(EPath.SLEEP_HISTORY.replace(":patientId", patientId));
  }, [navigate, patientId]);

  const handleViewNutritionHistory = useCallback(() => {
    navigate(EPath.NUTRITION_HISTORY.replace(":patientId", patientId));
  }, [navigate, patientId]);

  const handleViewBodyMeasurementsHistory = useCallback(() => {
    navigate(EPath.BODY_MEASUREMENTS_HISTORY.replace(":patientId", patientId));
  }, [navigate, patientId]);

  const generalInformation = useAdminPatientGeneralInformation(patientId);
  const functionalLifestyle = useAdminPatientFunctionalLifestyle(patientId);
  const goalsOfTreatment = useAdminPatientGoalsOfTreatment(patientId);
  const presentingConcerns = useAdminPatientPresentingConcerns(patientId);

  const nutrition = useAdminPatientNutrition(patientId);
  const environment = useAdminPatientEnvironmentToxins(patientId);
  const familyHistory = useAdminPatientFamilyHistory(patientId);
  const socialHistory = useAdminPatientSocialHistory(patientId);
  const additionalNotes = useAdminPatientAdditionalNotes(patientId);
  const medicalHistory = useAdminPatientMedicalHistory(patientId);
  const medications = useAdminPatientMedications(patientId);

  const [selectedMedicalSection, setSelectedMedicalSection] = useState<string>(
    "functional-lifestyle",
  );
  const [selectedHistorySection, setSelectedHistorySection] =
    useState<string>("nutrition");

  const isActiveSharing =
    patient.status === PatientsPaginationRequestDtoStatusEnum.ActiveSharing;

  const tabs: TabConfig[] = useMemo(() => {
    return [
      {
        label: "Patient Details",
        content: !isActiveSharing ? (
          <DataNotAvailableAlert />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <SectionCard
              title="General Information"
              loading={generalInformation.loading}
              error={generalInformation.error}
              hasData={Boolean(generalInformation.data)}
              emptyMessage="General information is not available yet."
            >
              <GeneralInformationGrid
                data={generalInformation.data ?? null}
                patient={patient}
              />
            </SectionCard>

            <VitalSigns
              patientId={patientId}
              onViewHistory={handleViewVitalSignsHistory}
            />

            <Activities
              patientId={patientId}
              onViewHistory={handleViewActivityHistory}
            />

            <Sleep
              patientId={patientId}
              onViewHistory={handleViewSleepHistory}
            />

            <NutritionAndHydration
              patientId={patientId}
              onViewHistory={handleViewNutritionHistory}
            />

            <DailyWellBeingScore patientId={patientId} />

            <BodyMeasurements
              patientId={patientId}
              onViewHistory={handleViewBodyMeasurementsHistory}
            />

            <ReproductiveHealth patientId={patientId} />
          </Box>
        ),
      },
      {
        label: "Medical Assessments",
        content: !isActiveSharing ? (
          <DataNotAvailableAlert />
        ) : (
          <SidebarLayout
            sections={medicalAssessmentSections}
            selectedSectionId={selectedMedicalSection}
            onSectionChange={setSelectedMedicalSection}
            sidebarWidth={260}
          >
            {renderMedicalAssessmentContent(selectedMedicalSection, {
              concerns: presentingConcerns,
              goals: goalsOfTreatment.data ?? null,
              goalsLoading: goalsOfTreatment.loading,
              goalsError: goalsOfTreatment.error,
              functionalLifestyle: functionalLifestyle.data ?? null,
              functionalLifestyleLoading: functionalLifestyle.loading,
              functionalLifestyleError: functionalLifestyle.error,
            })}
          </SidebarLayout>
        ),
      },
      {
        label: "History & Lifestyle",
        content: !isActiveSharing ? (
          <DataNotAvailableAlert />
        ) : (
          <SidebarLayout
            sections={historySections}
            selectedSectionId={selectedHistorySection}
            onSectionChange={setSelectedHistorySection}
            sidebarWidth={260}
          >
            {renderHistoryContent(selectedHistorySection, {
              nutrition,
              environment,
              familyHistory,
              socialHistory,
              additionalNotes,
              medicalHistory,
              medications,
            })}
          </SidebarLayout>
        ),
      },
      {
        label: "Patient Genetics",
        content: !isActiveSharing ? (
          <DataNotAvailableAlert />
        ) : (
          <PatientGenetics patientId={patientId} />
        ),
      },
      {
        label: "Patient Biomarkers",
        content: !isActiveSharing ? (
          <DataNotAvailableAlert />
        ) : (
          <PatientBiomarkers patientId={patientId} />
        ),
      },
    ];
  }, [
    generalInformation,
    functionalLifestyle,
    goalsOfTreatment,
    presentingConcerns,
    nutrition,
    environment,
    familyHistory,
    socialHistory,
    additionalNotes,
    medicalHistory,
    medications,
    selectedMedicalSection,
    selectedHistorySection,
    isActiveSharing,
    patient,
    patientId,
    handleViewVitalSignsHistory,
    handleViewActivityHistory,
    handleViewSleepHistory,
    handleViewNutritionHistory,
    handleViewBodyMeasurementsHistory,
  ]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        height: "100%",
        width: "100%",
      }}
    >
      <HeaderSection
        patient={patient}
        hasRequestedSuccessfullyBefore={hasRequestedSuccessfullyBefore}
        onCancelRequest={onCancelRequest}
        onRequest={onRequest}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          width: "100%",
        }}
      >
        <CustomTabs tabs={tabs} />
      </Box>
    </Box>
  );
};

export default AdminPatientDetail;

const DataNotAvailableAlert: React.FC = () => {
  const { colors } = useCelleraTheme();

  return (
    <Alert
      severity="info"
      sx={{
        borderRadius: 1,
        backgroundColor: colors.primary.light,
        border: `1px solid ${colors.primary.main}`,
        "& .MuiAlert-icon": {
          color: colors.primary.main,
        },
      }}
    >
      <AlertTitle
        sx={{
          color: colors.primary.main,
          fontWeight: 600,
        }}
      >
        Information
      </AlertTitle>
      <Typography variant="body1">
        Patient data is not yet available. Please wait for the patient to
        confirm sharing.
      </Typography>
    </Alert>
  );
};

const HeaderSection: React.FC<{
  patient: Patient;
  hasRequestedSuccessfullyBefore: boolean;
  onCancelRequest?: () => void;
  onRequest?: () => void;
}> = ({
  patient,
  hasRequestedSuccessfullyBefore,
  onCancelRequest,
  onRequest,
}) => {
  const { colors } = useCelleraTheme();
  const patientName = formatPatientName(patient);

  const getStatusColor = useCallback(
    (status?: string) => {
      switch (status) {
        case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
          return {
            border: `1px solid ${colors.success[700]}`,
            backgroundColor: colors.success[100],
            color: colors.success[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Revoked:
          return {
            border: `1px solid ${colors.error[700]}`,
            backgroundColor: colors.error[100],
            color: colors.error[700],
          };
        case PatientsPaginationRequestDtoStatusEnum.Requesting:
          return {
            border: `1px solid ${colors.warning[700]}`,
            backgroundColor: colors.warning[100],
            color: colors.warning[700],
          };
        default:
          return {
            p: 0,
            fontSize: 14,
          };
      }
    },
    [colors],
  );

  const statusMeta = getStatusMeta(patient.status);
  const formattedBirthday = patient.birthDate
    ? formatBirthDate(patient.birthDate)
    : "N/A";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: colors.border.main,
        bgcolor: colors.background.paper,
        borderRadius: 1,
        p: 2,
      }}
    >
      <Box className="flex items-start justify-between gap-4">
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: colors.success[700],
              color: colors.text.inverse,
              fontWeight: 700,
            }}
          >
            {patient.photoUrl ? (
              <img
                src={patient.photoUrl}
                alt={patientName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Person sx={{ fontSize: 30 }} />
            )}
          </Avatar>

          <Box className="flex-1">
            <Typography
              variant="h4"
              sx={{ color: colors.text.primary, fontWeight: 700, mb: 0.5 }}
            >
              {patientName}
            </Typography>
            <Box className="flex flex-col gap-1">
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontWeight: 600 }}
              >
                <span>Email:</span> {patient.email ?? "N/A"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, fontWeight: 600 }}
              >
                <span>Birthday:</span> {formattedBirthday}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="flex flex-col gap-1.5 items-start">
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 600 }}
          >
            <span>Phone:</span> {patient.phone ?? "N/A"}
          </Typography>
          <Box className="flex items-center gap-2">
            <Typography
              variant="body2"
              sx={{ color: colors.text.secondary, fontWeight: 600 }}
            >
              <span>Status:</span>
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: 0.5,
                fontSize: 12,
                fontWeight: 600,
                ...getStatusColor(patient.status),
              }}
            >
              {statusMeta.label}
            </Box>
          </Box>
        </Box>

        {patient.status === PatientsPaginationRequestDtoStatusEnum.Requesting &&
          onCancelRequest && (
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelRequest}
                sx={{
                  textTransform: "none",
                  borderColor: colors.error[700],
                  color: colors.error[700],
                  fontWeight: 600,
                  borderRadius: "100px",
                  alignItems: "center",
                  display: "flex",
                  mt: 0.5,
                  "&:hover": {
                    borderColor: colors.error[700],
                    backgroundColor: colors.error[50],
                  },
                }}
              >
                Cancel Request
              </Button>
            </Box>
          )}

        {patient.status === PatientsPaginationRequestDtoStatusEnum.Revoked &&
          onRequest && (
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={onRequest}
                sx={{
                  textTransform: "none",
                  backgroundColor: colors.primary.main,
                  color: colors.primary.contrast,
                  fontWeight: 600,
                  borderRadius: "100px",
                  alignItems: "center",
                  display: "flex",
                  mt: 0.5,
                  "&:hover": {
                    backgroundColor: colors.primary.dark,
                  },
                }}
              >
                Request
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  );
};

const SectionCard: React.FC<{
  title: string;
  loading: boolean;
  error: string | null;
  hasData: boolean;
  emptyMessage?: string;
  children: React.ReactNode;
}> = ({ title, loading, error, hasData, emptyMessage, children }) => {
  if (loading) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "center",
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary">
          Loading {title.toLowerCase()}...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!hasData) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          p: 3,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage ?? "No data available for this section yet."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Typography variant="h6">{title}</Typography>
      <Divider />
      {children}
    </Box>
  );
};

const InfoGrid: React.FC<{
  items: { label: string; value: string }[];
}> = ({ items }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
      gap: 2,
    }}
  >
    {items.map((item) => (
      <Box key={item.label}>
        <Typography variant="caption" color="textSecondary">
          {item.label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {item.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const GeneralInformationGrid: React.FC<{
  data: GeneralInformationResponseDto | null;
  patient: Patient;
}> = ({ data, patient }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "First name", value: patient.firstName ?? DEFAULT_EMPTY_VALUE },
    { label: "Middle name", value: patient.middleName ?? DEFAULT_EMPTY_VALUE },
    { label: "Last name", value: patient.lastName ?? DEFAULT_EMPTY_VALUE },
    { label: "Email", value: data.email ?? DEFAULT_EMPTY_VALUE },
    { label: "Phone", value: data.phone ?? DEFAULT_EMPTY_VALUE },
    { label: "Date of birth", value: formatOptionalDate(data.birthDate) },
    { label: "Gender", value: startCase(data.gender ?? "unknown") },
    {
      label: "Primary care phone",
      value: data.primaryCarePhone ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Emergency contact",
      value: data.emergencyContactName ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Emergency contact phone",
      value: data.emergencyContactPhone ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Weight",
      value: data.weight
        ? `${data.weight.value} ${data.weight.unit}`
        : DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Height",
      value: data.height
        ? `${data.height.feet}' ${data.height.inches}"`
        : DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries} />;
};

const FunctionalLifestyleGrid: React.FC<{
  data: FunctionalLifestyleResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "Energy levels", value: formatScore(data.energyLevels) },
    {
      label: "Mental clarity & focus",
      value: formatScore(data.mentalClarityFocus),
    },
    {
      label: "Mood & emotional wellbeing",
      value: formatScore(data.moodEmotionalWellbeing),
    },
    {
      label: "Libido & sexual health",
      value: formatScore(data.libidoSexualHealth),
    },
    { label: "Digestion", value: formatScore(data.digestion) },
    { label: "Immune function", value: formatScore(data.immuneFunction) },
    { label: "Fitness level", value: formatScore(data.fitnessLevel) },
    { label: "Skin appearance", value: formatScore(data.skinAppearance) },
    { label: "Hair health", value: formatScore(data.hairHealth) },
    { label: "Sleep quality", value: formatScore(data.sleep) },
    {
      label: "Additional information",
      value: data.additionalInformation ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries} />;
};

const GoalsList: React.FC<{ data: GoalsOfTreatmentResponseDto | null }> = ({
  data,
}) => {
  if (!data?.healthGoals?.length) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {data.healthGoals.map((goal, index) => (
        <Box
          key={goal.id}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Typography variant="subtitle2">{`Goal #${index + 1}`}</Typography>
          <Typography variant="body2" color="textSecondary">
            {goal.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const medicalAssessmentSections: SidebarSection[] = [
  {
    id: "functional-lifestyle",
    label: "Functional & Lifestyle",
  },
  {
    id: "goals-of-treatment",
    label: "Goals of Treatment",
  },
  {
    id: "presenting-concerns",
    label: "Presenting Concerns",
  },
];

const historySections: SidebarSection[] = [
  {
    id: "nutrition",
    label: "Nutrition & Diet",
  },
  {
    id: "environment",
    label: "Environment & Toxins",
  },
  {
    id: "family-history",
    label: "Family History",
  },
  {
    id: "social-history",
    label: "Social History",
  },
  {
    id: "additional-notes",
    label: "Additional Notes",
  },
  {
    id: "medical-history",
    label: "Medical History",
  },
  {
    id: "medications",
    label: "Medications",
  },
];

type MedicalAssessmentContext = {
  concerns: ReturnType<typeof useAdminPatientPresentingConcerns>;
  goals: GoalsOfTreatmentResponseDto | null;
  goalsLoading: boolean;
  goalsError: string | null;
  functionalLifestyle: FunctionalLifestyleResponseDto | null;
  functionalLifestyleLoading: boolean;
  functionalLifestyleError: string | null;
};

function renderMedicalAssessmentContent(
  sectionId: string,
  context: MedicalAssessmentContext,
) {
  const {
    concerns,
    goals,
    goalsLoading,
    goalsError,
    functionalLifestyle,
    functionalLifestyleLoading,
    functionalLifestyleError,
  } = context;
  switch (sectionId) {
    case "functional-lifestyle":
      return (
        <SectionCard
          title="Functional & Lifestyle Assessment"
          loading={functionalLifestyleLoading}
          error={functionalLifestyleError}
          hasData={Boolean(functionalLifestyle)}
          emptyMessage="Functional lifestyle responses are not available."
        >
          <FunctionalLifestyleGrid data={functionalLifestyle} />
        </SectionCard>
      );
    case "goals-of-treatment":
      return (
        <SectionCard
          title="Goals of Treatment"
          loading={goalsLoading}
          error={goalsError}
          hasData={Boolean(goals?.healthGoals?.length)}
          emptyMessage="No goals of treatment recorded."
        >
          <GoalsList data={goals} />
        </SectionCard>
      );
    case "presenting-concerns":
    default:
      return (
        <SectionCard
          title="Presenting Concerns"
          loading={concerns.loading}
          error={concerns.error}
          hasData={Boolean(concerns.data?.presentingConcerns?.length)}
          emptyMessage="Presenting concerns have not been captured yet."
        >
          <PresentingConcernsList data={concerns.data ?? null} />
        </SectionCard>
      );
  }
}

type HistoryContext = {
  nutrition: ReturnType<typeof useAdminPatientNutrition>;
  environment: ReturnType<typeof useAdminPatientEnvironmentToxins>;
  familyHistory: ReturnType<typeof useAdminPatientFamilyHistory>;
  socialHistory: ReturnType<typeof useAdminPatientSocialHistory>;
  additionalNotes: ReturnType<typeof useAdminPatientAdditionalNotes>;
  medicalHistory: ReturnType<typeof useAdminPatientMedicalHistory>;
  medications: ReturnType<typeof useAdminPatientMedications>;
};

function renderHistoryContent(sectionId: string, context: HistoryContext) {
  const {
    nutrition,
    environment,
    familyHistory,
    socialHistory,
    additionalNotes,
    medicalHistory,
    medications,
  } = context;
  switch (sectionId) {
    case "nutrition":
      return (
        <SectionCard
          title="Nutrition & Diet"
          loading={nutrition.loading}
          error={nutrition.error}
          hasData={Boolean(nutrition.data)}
          emptyMessage="Nutrition details have not been provided."
        >
          <NutritionGrid data={nutrition.data ?? null} />
        </SectionCard>
      );
    case "environment":
      return (
        <SectionCard
          title="Environment & Toxins"
          loading={environment.loading}
          error={environment.error}
          hasData={Boolean(environment.data)}
          emptyMessage="Environment and toxin exposure has not been recorded."
        >
          <EnvironmentGrid data={environment.data ?? null} />
        </SectionCard>
      );
    case "family-history":
      return (
        <SectionCard
          title="Family History"
          loading={familyHistory.loading}
          error={familyHistory.error}
          hasData={Boolean(familyHistory.data)}
          emptyMessage="Family history details are not available."
        >
          <FamilyHistoryGrid data={familyHistory.data ?? null} />
        </SectionCard>
      );
    case "social-history":
      return (
        <SectionCard
          title="Social History"
          loading={socialHistory.loading}
          error={socialHistory.error}
          hasData={Boolean(socialHistory.data)}
          emptyMessage="Social history responses are unavailable."
        >
          <SocialHistoryGrid data={socialHistory.data ?? null} />
        </SectionCard>
      );
    case "additional-notes":
      return (
        <SectionCard
          title="Additional Notes"
          loading={additionalNotes.loading}
          error={additionalNotes.error}
          hasData={Boolean(additionalNotes.data)}
          emptyMessage="There are no additional notes on record."
        >
          <AdditionalNotesView data={additionalNotes.data ?? null} />
        </SectionCard>
      );
    case "medical-history":
      return (
        <SectionCard
          title="Medical History"
          loading={medicalHistory.loading}
          error={medicalHistory.error}
          hasData={Boolean(medicalHistory.data)}
          emptyMessage="Medical history has not been captured yet."
        >
          <MedicalHistoryView data={medicalHistory.data ?? null} />
        </SectionCard>
      );
    case "medications":
    default:
      return (
        <SectionCard
          title="Current Medications"
          loading={medications.loading}
          error={medications.error}
          hasData={medications.data.length > 0}
          emptyMessage="No medications are currently on record."
        >
          <MedicationList data={medications.data} />
        </SectionCard>
      );
  }
}

const PresentingConcernsList: React.FC<{
  data: PresentingConcernsResponseDto | null;
}> = ({ data }) => {
  if (!data?.presentingConcerns?.length) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {data.presentingConcerns.map((concern, index) => (
        <Box
          key={concern.id ?? `concern-${index}`}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Typography variant="subtitle2">
            Health Concern #{index + 1}
          </Typography>
          <Typography variant="body2" color="textPrimary">
            {concern.primaryHealthConcerns ?? DEFAULT_EMPTY_VALUE}
          </Typography>
          <InfoRow
            label="When first noticed"
            value={concern.whenFirstNoticed ?? DEFAULT_EMPTY_VALUE}
          />
          <InfoRow
            label="Treatments tried"
            value={concern.treatmentsTried ?? DEFAULT_EMPTY_VALUE}
          />
        </Box>
      ))}
    </Stack>
  );
};

const NutritionGrid: React.FC<{
  data: NutritionAndDietResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "Diet type", value: data.dietType },
    { label: "Breakfast", value: data.breakfast },
    { label: "Lunch", value: data.lunch },
    { label: "Dinner", value: data.dinner },
    { label: "Snacks & beverages", value: data.snacksOrBeverages },
    { label: "Water per day", value: data.waterPerDay },
    { label: "Food insensitivities", value: data.foodInsensitivities },
    { label: "Additional information", value: data.additionalInformation },
  ];

  return <InfoGrid items={entries.map(mapToInfoItem)} />;
};

const EnvironmentGrid: React.FC<{
  data: EnvironmentToxinsResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "Home environment", value: data.home },
    {
      label: "Other home details",
      value: data.homeOther ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Air/Water filters",
      value: data.airWaterFilters ? "Yes" : "No",
    },
    {
      label: "Chemical exposure",
      value: formatArrayValue(data.chemicalExposure),
    },
    {
      label: "Other chemical exposure",
      value: data.chemicalOther ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Additional information",
      value: data.additionalInformation ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries.map(mapToInfoItem)} />;
};

const FamilyHistoryGrid: React.FC<{
  data: FamilyHistoryResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "Cancer", value: data.cancer ?? DEFAULT_EMPTY_VALUE },
    { label: "Diabetes", value: data.diabetes ?? DEFAULT_EMPTY_VALUE },
    {
      label: "Autoimmune disease",
      value: data.autoimmuneDisease ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Heart disease", value: data.heartDisease ?? DEFAULT_EMPTY_VALUE },
    { label: "Stroke", value: data.stroke ?? DEFAULT_EMPTY_VALUE },
    { label: "Other", value: data.other ?? DEFAULT_EMPTY_VALUE },
    {
      label: "Additional information",
      value: data.additionalInformation ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries.map(mapToInfoItem)} />;
};

const SocialHistoryGrid: React.FC<{
  data: SocialHistoryResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    { label: "Relationship status", value: data.relationship },
    { label: "Has children", value: data.hasChildren ? "Yes" : "No" },
    {
      label: "Number of children",
      value: data.numberOfChildren?.toString() ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Ages of children",
      value: data.agesOfChildren ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Tobacco use", value: data.tobaccoUse },
    {
      label: "Tobacco use amount",
      value: data.tobaccoUseAmount ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Alcohol use", value: data.alcoholUse },
    {
      label: "Alcohol use amount",
      value: data.alcoholUseAmount ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Cannabis/Drug use",
      value: data.cannabisOrDrugUse ? "Yes" : "No",
    },
    {
      label: "Cannabis/Drug use amount",
      value: data.cannabisOrDrugUseAmount ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Caffeine use", value: data.caffeineUse },
    {
      label: "Caffeine source",
      value: data.caffeineSource ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Sleep hours", value: data.sleepHours },
    { label: "Sleep quality", value: data.sleepQuality },
    { label: "Sleep trouble", value: data.sleepTrouble ? "Yes" : "No" },
    {
      label: "Physical activity frequency",
      value: data.physicalActivityFrequency,
    },
    {
      label: "Physical activity type",
      value: data.physicalActivityType ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Physical activity minutes per session",
      value: data.physicalActivityMinutesPerSession ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Stress level", value: data.stressLevel },
    { label: "Relationship stresses", value: data.relationshipStresses },
    { label: "Stress source", value: data.stressSource ?? DEFAULT_EMPTY_VALUE },
    {
      label: "Stress coping methods",
      value: data.stressCopingMethods ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Support system",
      value: data.hasSupportSystem ? "Yes" : "No",
    },
    {
      label: "Additional information",
      value: data.additionalInformation ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries.map(mapToInfoItem)} />;
};

const AdditionalNotesView: React.FC<{
  data: ReturnType<typeof useAdminPatientAdditionalNotes>["data"];
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const entries = [
    {
      label: "Important information",
      value: data.additionalImportantInformation ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Comfortable with injectables",
      value: formatOptionalBoolean(data.comfortableWithInjectables),
    },
    {
      label: "Expected monthly budget",
      value: data.expectedMonthlyBudget ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Therapies tried previously",
      value: data.therapiesTriedPreviously ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  return <InfoGrid items={entries.map(mapToInfoItem)} />;
};

const MedicalHistoryView: React.FC<{
  data: MedicalHistoryResponseDto | null;
}> = ({ data }) => {
  if (!data) {
    return null;
  }

  const historyEntries = [
    {
      label: "Surgeries / Hospitalizations",
      value: data.surgeriesHospitalizations ?? DEFAULT_EMPTY_VALUE,
    },
    {
      label: "Significant injuries",
      value: data.significantInjuries ?? DEFAULT_EMPTY_VALUE,
    },
    { label: "Allergies", value: data.allergies ?? DEFAULT_EMPTY_VALUE },
    {
      label: "Other notes",
      value: data.pastMedicalHistory?.additionalNotes ?? DEFAULT_EMPTY_VALUE,
    },
  ];

  const conditionEntries = Object.entries(data.pastMedicalHistory ?? {})
    .filter(
      ([key]) =>
        key !== "additionalNotes" && key !== "other" && key !== "cancerType",
    )
    .map(([key, value]) => ({
      label: startCase(key),
      value: formatConditionValue(value),
    }));

  return (
    <Stack spacing={2}>
      <InfoGrid items={historyEntries.map(mapToInfoItem)} />
      {conditionEntries.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2">Conditions</Typography>
          <InfoGrid items={conditionEntries.map(mapToInfoItem)} />
        </>
      )}
      {data.pastMedicalHistory?.other && (
        <InfoRow
          label="Other conditions"
          value={data.pastMedicalHistory.other}
        />
      )}
      {data.pastMedicalHistory?.cancerType && (
        <InfoRow
          label="Cancer type"
          value={data.pastMedicalHistory.cancerType}
        />
      )}
    </Stack>
  );
};

const MedicationList: React.FC<{
  data: MedicationStatementResponseDto[];
}> = ({ data }) => {
  if (data.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {data.map((medication) => (
        <Box
          key={medication.id}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Typography variant="subtitle2">{medication.name}</Typography>
          <InfoRow label="Dose" value={medication.dose} />
          <InfoRow label="Frequency" value={medication.frequency} />
          <InfoRow
            label="Purpose"
            value={medication.purpose ?? DEFAULT_EMPTY_VALUE}
          />
        </Box>
      ))}
    </Stack>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

function getStatusMeta(status: PatientsPaginationRequestDtoStatusEnum): {
  label: string;
  color: "default" | "success" | "warning" | "error";
} {
  switch (status) {
    case PatientsPaginationRequestDtoStatusEnum.ActiveSharing:
      return { label: "Active sharing", color: "success" };
    case PatientsPaginationRequestDtoStatusEnum.Requesting:
      return { label: "Requesting access", color: "warning" };
    case PatientsPaginationRequestDtoStatusEnum.Revoked:
      return { label: "Sharing revoked", color: "error" };
    default:
      return { label: startCase(status ?? "unknown"), color: "default" };
  }
}

function formatScore(score: number): string {
  if (score === undefined || score === null) {
    return DEFAULT_EMPTY_VALUE;
  }
  return `${score}/10`;
}

function formatOptionalDate(
  value: Date | string | null | undefined,
  fallback: string = DEFAULT_EMPTY_VALUE,
): string {
  if (!value) {
    return fallback;
  }

  const dateValue = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(dateValue.getTime())) {
    return fallback;
  }

  return formatBirthDate(dateValue.toISOString());
}

function formatArrayValue(values?: string[] | null): string {
  if (!values || values.length === 0) {
    return DEFAULT_EMPTY_VALUE;
  }
  return values.join(", ");
}

function startCase(value: string): string {
  const sanitized = value.replaceAll("_", " ").replaceAll("-", " ");

  let spaced = "";
  for (let index = 0; index < sanitized.length; index += 1) {
    const char = sanitized[index] ?? "";
    const prevChar = sanitized[index - 1] ?? "";
    const isUppercase = char >= "A" && char <= "Z";
    const prevIsLowerAlpha =
      (prevChar >= "a" && prevChar <= "z") ||
      (prevChar >= "0" && prevChar <= "9");

    if (index > 0 && isUppercase && prevIsLowerAlpha) {
      spaced += " ";
    }

    spaced += char;
  }

  const collapsed = spaced.split(/\s+/).filter(Boolean).join(" ").trim();

  if (!collapsed) {
    return collapsed;
  }

  return collapsed.charAt(0).toUpperCase() + collapsed.slice(1);
}

function mapToInfoItem(item: {
  label: string;
  value: string | null | undefined;
}) {
  return {
    label: item.label,
    value:
      typeof item.value === "string" && item.value.length > 0
        ? item.value
        : DEFAULT_EMPTY_VALUE,
  };
}

function formatOptionalBoolean(
  value: boolean | null | undefined,
  fallback: string = DEFAULT_EMPTY_VALUE,
): string {
  if (value === undefined || value === null) {
    return fallback;
  }
  return value ? "Yes" : "No";
}

function formatConditionValue(value: unknown): string {
  if (typeof value === "boolean") {
    return formatOptionalBoolean(value);
  }
  if (value === undefined || value === null) {
    return DEFAULT_EMPTY_VALUE;
  }
  if (Array.isArray(value)) {
    const converted = value
      .map((entry) => formatConditionValue(entry))
      .filter((entry) => entry !== DEFAULT_EMPTY_VALUE);
    return converted.length > 0 ? converted.join(", ") : DEFAULT_EMPTY_VALUE;
  }
  if (typeof value === "object") {
    return DEFAULT_EMPTY_VALUE;
  }
  if (typeof value === "number" || typeof value === "bigint") {
    return value.toString();
  }
  if (typeof value === "string") {
    const text = value.trim();
    return text.length > 0 ? text : DEFAULT_EMPTY_VALUE;
  }
  return DEFAULT_EMPTY_VALUE;
}
